'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Crop, PixelCrop } from 'react-image-crop';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Asset } from './AssetItem';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface AssetModifyModalProps {
  asset: Asset | null;
  mediaUrl: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (croppedImageFile: File) => Promise<void>;
}

export default function AssetModifyModal({
  asset,
  mediaUrl,
  isOpen,
  onClose,
  onSave
}: AssetModifyModalProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const initialCrop: Crop = {
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  };
  
  const [crop, setCrop] = useState<Crop>(initialCrop);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);

  // Wrapper to prevent crop from being cleared
  const handleCropChange = useCallback((newCrop: Crop) => {
    // Only update if the crop has valid dimensions
    // This prevents the crop from disappearing when clicking outside
    if (newCrop && newCrop.width && newCrop.height) {
      setCrop(newCrop);
    }
  }, []);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop: Crop = {
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5
    };
    setCrop(crop);
  }, []);

  // Apply crop - generate preview
  const handleApply = async () => {
    if (!completedCrop || !imgRef.current) {
      toast.error('Please select an area to crop');
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
      
      canvas.width = completedCrop.width;
      canvas.height = completedCrop.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      ctx.drawImage(
        imgRef.current,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width,
        completedCrop.height
      );

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas is empty'));
            }
          },
          'image/jpeg',
          0.95
        );
      });

      // Create preview URL
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);

      // Create File from blob
      const file = new File([blob], asset?.name || 'modified-image.jpg', {
        type: 'image/jpeg',
      });
      setCroppedFile(file);
      
      toast.success('Preview generated! Review and save or revert.');
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    }
  };

  // Revert to original
  const handleRevert = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setCroppedFile(null);
    setCrop({
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5
    });
    setCompletedCrop(null);
    toast.success('Reverted to original');
  };

  // Save to backend
  const handleSave = async () => {
    if (!croppedFile) {
      toast.error('Please apply changes first');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(croppedFile);
      // Clean up
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      setCroppedFile(null);
      onClose();
    } catch (error) {
      console.error('Error saving cropped image:', error);
      toast.error('Failed to save modified image');
    } finally {
      setIsSaving(false);
    }
  };

  // Clean up on close
  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setCroppedFile(null);
    onClose();
  };

  if (!isOpen || !asset || !mediaUrl) return null;

  const ReactCropAny = ReactCrop as any;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">
            {previewUrl ? 'Review Changes' : 'Modify Image'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {!previewUrl ? (
            // Step 1: Crop editor
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Adjust the crop area to modify your image. You can resize and reposition the selection.
              </p>
              <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                {mediaUrl && (
                  <ReactCropAny
                    crop={crop}
                    onChange={(_: any, percentCrop: Crop) => handleCropChange(percentCrop)}
                    onComplete={(c: PixelCrop) => {
                      // Only update if we have a valid crop
                      if (c && c.width && c.height) {
                        setCompletedCrop(c);
                      }
                    }}
                    aspect={undefined}
                    minWidth={50}
                    minHeight={50}
                    ruleOfThirds
                  >
                    <img
                      ref={imgRef}
                      alt="Crop preview"
                      src={mediaUrl}
                      onLoad={onImageLoad}
                      className="max-h-[500px] max-w-full"
                    />
                  </ReactCropAny>
                )}
              </div>
            </div>
          ) : (
            // Step 2: Preview result
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Preview your changes. Click <strong>Save</strong> to apply permanently or <strong>Revert</strong> to go back.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {/* Original */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Original</h4>
                  <div className="bg-gray-100 rounded-lg p-4 flex justify-center">
                    <img
                      src={mediaUrl}
                      alt="Original"
                      className="max-h-[400px] max-w-full object-contain"
                    />
                  </div>
                </div>
                {/* Modified */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Modified</h4>
                  <div className="bg-gray-100 rounded-lg p-4 flex justify-center">
                    <img
                      src={previewUrl}
                      alt="Modified"
                      className="max-h-[400px] max-w-full object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            
            <div className="flex space-x-3">
              {!previewUrl ? (
                // Step 1: Show Apply button
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={!completedCrop}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Changes
                </button>
              ) : (
                // Step 2: Show Revert and Save buttons
                <>
                  <button
                    type="button"
                    onClick={handleRevert}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    Revert
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save to Backend'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}